import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
// Added RefreshCw for the Refresh SKU button
import { Upload as UploadIcon, Settings, AlertTriangle, CheckCircle, X, Loader2, RefreshCw } from 'lucide-react';

import UploadCard from '../pages/AutomatedPOImport/components/UploadCard';
import UploadHistory from '../pages/AutomatedPOImport/components/UploadHistory';

// Import fetchErpagCategories from the ERPAG API handler
import { fetchErpagCategories } from '../api/erpag';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/';
const apiUrl = (url) => `${API_BASE}${url}`;

const getAuthHeaders = () => {
  const token =
    localStorage.getItem('token') ||
    localStorage.getItem('jwt') ||
    localStorage.getItem('accessToken') ||
    localStorage.getItem('authToken') ||
    localStorage.getItem('userToken') ||
    '';
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getStoredUserName = () => {
  try {
    const userStr =
      localStorage.getItem('user') ||
      localStorage.getItem('userInfo') ||
      localStorage.getItem('authUser') ||
      localStorage.getItem('currentUser');
    if (userStr) {
      const u = JSON.parse(userStr);
      if (u.fullName) return u.fullName;
      if (u.name) return u.name;
      const combined = `${u.firstName || ''} ${u.lastName || ''}`.trim();
      if (combined) return combined;
      if (u.username) return u.username;
      if (u.email) return u.email;
    }
  } catch (_) {}
  return '';
};

// ============================================================
// PO RULE OPTIONS
// ============================================================
const UOM_OPTIONS = [
  { value: 'DEFAULT_UOM', label: 'Default UOM (from ERPAG)', requiresMultiple: false },
  { value: 'SECOND_UOM', label: 'Second UOM (from ERPAG)', requiresMultiple: false },
  { value: 'THIRD_UOM', label: 'Third UOM (from ERPAG)', requiresMultiple: false },
  { value: 'UNIT_OVERRIDE', label: 'Unit override', requiresMultiple: true },
  { value: 'CS_OVERRIDE', label: 'CS override', requiresMultiple: true },
];

// ============================================================
// HELPERS
// ============================================================
const getCategoryName = (category) => {
  if (typeof category === 'string') return category.trim();
  if (!category || typeof category !== 'object') return '';
  return String(
    category.name || category.category || category.categoryName || category.category_name ||
    category.erpCategory || category.erp_category || category.CategoryName || category.Category ||
    category['Category Name'] || category['category name'] || category.NAME || category.title || ''
  ).trim();
};

const getCustomerName = (upload) => {
  return upload?.structuredData?.billTo?.name || upload?.customer || '';
};

const getBillToName = (upload) => {
  return upload?.structuredData?.billTo?.name || upload?.customer || '';
};

const getShipTo = (upload) => {
  return upload?.structuredData?.shipTo || null;
};

const hasShipToAddress = (upload) => {
  const shipTo = getShipTo(upload);
  if (!shipTo) return false;
  const name = String(shipTo.name || '').trim();
  const street = shipTo.streetLines?.length > 0 ? shipTo.streetLines.join(', ') : shipTo.address1 || shipTo.address || '';
  const city = String(shipTo.city || '').trim();
  const postalCode = String(shipTo.postalCode || '').trim();
  const country = String(shipTo.countryCode || shipTo.country || '').trim();
  
  const state = String(shipTo.stateProvince || shipTo.region || shipTo.state || '').trim();
  
  return Boolean(name && street && city && postalCode && country && state);
};

const getShipToAddressText = (upload) => {
  const shipTo = getShipTo(upload);
  if (!shipTo) return '';
  const street = shipTo.streetLines?.length > 0 ? shipTo.streetLines.join(', ') : shipTo.address1 || shipTo.address || '';
  return [
    shipTo.name, street,
    [shipTo.city, shipTo.region].filter(Boolean).join(', '),
    shipTo.postalCode, shipTo.countryCode || shipTo.country,
  ].filter(Boolean).join(', ');
};

const getRuleLabel = (rule) => {
  const found = UOM_OPTIONS.find((option) => option.value === rule?.unitOrdering);
  return found?.label || rule?.unitOrdering || 'Not configured';
};

const getItems = (upload) => {
  return upload?.structuredData?.items || [];
};

// ============================================================
// MAIN COMPONENT
// ============================================================
const SalesERPag = () => {

  // ==========================================================
  // INITIAL LOAD STATE
  // ==========================================================
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // ==========================================================
  // UPLOAD STATE
  // ==========================================================
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // ==========================================================
  // REFRESH SKU STATE
  // ==========================================================
  const [isRefreshingSkus, setIsRefreshingSkus] = useState(false);

  // ==========================================================
  // HISTORY STATE
  // ==========================================================
  const [uploads, setUploads] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // ==========================================================
  // PAGINATION STATE
  // ==========================================================
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // ==========================================================
  // MODALS
  // ==========================================================
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isRulesModalOpen, setIsRulesModalOpen] = useState(false);
  const [isRuleFormOpen, setIsRuleFormOpen] = useState(false);
  const [isOrderDetailsOpen, setIsOrderDetailsOpen] = useState(false);

  // ==========================================================
  // RULE STATE
  // ==========================================================
  const [poRules, setPoRules] = useState([]);
  const [ruleLoading, setRuleLoading] = useState(false);
  const [ruleSaving, setRuleSaving] = useState(false);
  const [ruleError, setRuleError] = useState('');
  const [editingRuleId, setEditingRuleId] = useState(null);

  const emptyRule = { customerFamily: '', customerName: '', erpagCategory: '', unitOrdering: 'DEFAULT_UOM', multiple: '' };
  const [ruleForm, setRuleForm] = useState(emptyRule);

  // ==========================================================
  // CUSTOMER FAMILIES STATE
  // ==========================================================
  const [customerFamilies, setCustomerFamilies] = useState([]);
  const [familiesLoading, setFamiliesLoading] = useState(false);

  // ==========================================================
  // ERPAG CATEGORIES STATE
  // ==========================================================
  const [erpCategories, setErpCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // ==========================================================
  // ORDER DETAILS
  // ==========================================================
  const [selectedOrder, setSelectedOrder] = useState(null);

  // ==========================================================
  // API CALL
  // ==========================================================
  const apiCall = async (method, url, body) => {
    const config = { method, url: apiUrl(url), headers: getAuthHeaders() };
    if (body !== undefined) config.data = body;
    const response = await axios(config);
    return response.data;
  };

  // ==========================================================
  // FETCH HISTORY
  // ==========================================================
  const fetchHistory = async () => {
    try {
      const response = await axios.get(apiUrl('api/po/history'), {
        headers: getAuthHeaders()
      });
      if (response.data.success) {
        setUploads(response.data.uploads || response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching PO history:', err);
    } finally {
      setIsInitialLoad(false);
    }
  };

  // ==========================================================
  // REFRESH SKU DATABSAE
  // ==========================================================
  const handleRefreshSkus = async () => {
    const confirmed = window.confirm("Are you sure you want to proceed? This action will update the database with the latest SKU data retrieved from ERPAG, including SKU, description, sapSobeys, sapMetro, and sapLoblaws fields.");
    if (!confirmed) return;

    setIsRefreshingSkus(true);
    try {
      const response = await axios.post(apiUrl('api/po/refresh-skus'), {}, {
        headers: getAuthHeaders()
      });
      
      if (response.data.success) {
        alert(`Success: Database updated successfully! (${response.data.count} records processed)`);
      } else {
        alert(`Error: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error refreshing SKUs:', error);
      alert(`Error: ${error.response?.data?.message || error.message || 'Failed to refresh SKUs.'}`);
    } finally {
      setIsRefreshingSkus(false);
    }
  };

  // ==========================================================
  // FETCH PO RULES
  // ==========================================================
  const fetchPoRules = async () => {
    setRuleLoading(true);
    setRuleError('');
    try {
      const response = await apiCall('get', 'api/po-rules');
      if (response.success) {
        setPoRules(response.rules || response.data || []);
      } else {
        setRuleError(response.message || 'Failed to load PO rules.');
      }
    } catch (error) {
      console.error('Error fetching PO rules:', error);
      setRuleError(error.response?.data?.message || 'Failed to load PO rules.');
    } finally {
      setRuleLoading(false);
    }
  };

  // ==========================================================
  // FETCH CUSTOMER FAMILIES
  // ==========================================================
  const fetchCustomerFamilies = async () => {
    setFamiliesLoading(true);
    try {
      const response = await axios.get(apiUrl('api/analytics/filters'), {
        headers: getAuthHeaders()
      });
      const families = response.data?.families || [];
      setCustomerFamilies(families);
    } catch (error) {
      console.error('Error fetching customer families:', error);
      setCustomerFamilies([]);
    } finally {
      setFamiliesLoading(false);
    }
  };

  // ==========================================================
  // FETCH ERPAG CATEGORIES
  // ==========================================================
  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const response = await fetchErpagCategories();
      if (response.success && Array.isArray(response.data)) {
        const catNames = Array.from(new Set(response.data.map(getCategoryName).filter(Boolean))).sort((a, b) => a.localeCompare(b));
        setErpCategories(catNames);
      } else {
        setErpCategories([]);
      }
    } catch (error) {
      console.error('Error in SalesERPag fetchCategories:', error);
      setErpCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  // ==========================================================
  // INITIAL LOAD
  // ==========================================================
  useEffect(() => {
    fetchHistory();
    const interval = setInterval(fetchHistory, 10000);
    return () => clearInterval(interval);
  }, []);

  // ==========================================================
  // FILE HANDLING
  // ==========================================================
  const handleFileSelect = (file, errorMsg = null) => {
    if (errorMsg) {
      setUploadStatus('error');
      setErrorMessage(errorMsg);
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
    setUploadStatus('idle');
    setErrorMessage('');
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setUploadStatus('idle');
    setErrorMessage('');
  };

  // ==========================================================
  // UPLOAD
  // ==========================================================
  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploadStatus('uploading');
    setUploadProgress(0);
    setErrorMessage('');
 
    const formData = new FormData();
    
    // Append the text fields BEFORE the file so the backend Multer parser catches them.
    const userName = getStoredUserName();
    if (userName) {
      formData.append('uploadedByName', userName);
      formData.append('importedByName', userName); // Fallback field
    }
    
    formData.append('file', selectedFile);

    try {
      await axios.post(apiUrl('api/po/upload'), formData, {
        headers: getAuthHeaders(),
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || selectedFile.size));
          setUploadProgress(percentCompleted);
        },
      });
      handleUploadSuccess();
    } catch (err) {
      console.error('Upload error:', err);
      setUploadStatus('error');
      setErrorMessage(err.response?.data?.message || 'Server error occurred during upload.');
    }
  };

  const handleUploadSuccess = () => {
    setUploadStatus('success');
    fetchHistory();
    setTimeout(() => {
      setSelectedFile(null);
      setUploadStatus('idle');
      setUploadProgress(0);
      setIsUploadModalOpen(false);
    }, 3000);
  };

  // ==========================================================
  // RULE FORM
  // ==========================================================
  const openAddRule = () => {
    setEditingRuleId(null);
    setRuleForm(emptyRule);
    setRuleError('');
    setIsRuleFormOpen(true);
  };

  const openEditRule = (rule) => {
    setEditingRuleId(rule._id || rule.id);
    setRuleForm({
      customerFamily: rule.customerFamily || '',
      customerName: rule.customerName || '',
      erpagCategory: rule.erpagCategory || '',
      unitOrdering: rule.unitOrdering || 'DEFAULT_UOM',
      multiple: rule.multiple ?? '',
    });
    setRuleError('');
    setIsRuleFormOpen(true);
  };

  const closeRuleForm = () => {
    if (ruleSaving) return;
    setIsRuleFormOpen(false);
    setEditingRuleId(null);
    setRuleForm(emptyRule);
  };

  const handleRuleChange = (field, value) => {
    if (field === 'customerFamily') {
      setRuleForm((previous) => ({ ...previous, customerFamily: value, customerName: '' }));
      return;
    }
    setRuleForm((previous) => ({ ...previous, [field]: value }));
  };

  const selectedUomOption = UOM_OPTIONS.find((option) => option.value === ruleForm.unitOrdering);

  const selectedFamilyObj = useMemo(() => {
    if (!ruleForm.customerFamily) return null;
    return customerFamilies.find((f) => f.name === ruleForm.customerFamily) || null;
  }, [customerFamilies, ruleForm.customerFamily]);

  const familyCustomers = useMemo(() => {
    if (!selectedFamilyObj) return [];
    return (selectedFamilyObj.customers || []).slice().sort((a, b) => a.localeCompare(b));
  }, [selectedFamilyObj]);

  const sortedFamilies = useMemo(() => {
    return (customerFamilies || []).slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [customerFamilies]);

  // ==========================================================
  // SAVE PO RULE
  // ==========================================================
  const savePoRule = async () => {
    setRuleError('');
    if (!ruleForm.customerFamily && !ruleForm.customerName) {
      setRuleError('Please provide a customer family or individual customer.');
      return;
    }
    if (!ruleForm.erpagCategory.trim()) {
      setRuleError('ERPAG category is required.');
      return;
    }
    if (selectedUomOption?.requiresMultiple) {
      const multiple = Number(ruleForm.multiple);
      if (!Number.isFinite(multiple) || multiple <= 0) {
        setRuleError('Multiple must be greater than 0 for Unit override or CS override.');
        return;
      }
    }

    setRuleSaving(true);
    try {
      const payload = {
        customerFamily: ruleForm.customerFamily.trim(),
        customerName: ruleForm.customerName.trim(),
        erpagCategory: ruleForm.erpagCategory.trim(),
        unitOrdering: ruleForm.unitOrdering,
        multiple: selectedUomOption?.requiresMultiple ? Number(ruleForm.multiple) : null,
      };

      let response;
      if (editingRuleId) {
        response = await apiCall('put', `api/po-rules/${editingRuleId}`, payload);
      } else {
        response = await apiCall('post', 'api/po-rules', payload);
      }

      if (!response.success) throw new Error(response.message || 'Unable to save PO rule.');

      await fetchPoRules();
      setIsRuleFormOpen(false);
      setEditingRuleId(null);
      setRuleForm(emptyRule);
    } catch (error) {
      console.error('Save PO rule error:', error);
      setRuleError(error.response?.data?.message || error.message || 'Failed to save PO rule.');
    } finally {
      setRuleSaving(false);
    }
  };

  // ==========================================================
  // DELETE RULE
  // ==========================================================
  const deletePoRule = async (rule) => {
    const id = rule._id || rule.id;
    if (!id) return;

    const confirmed = window.confirm('Are you sure you want to delete this PO rule?');
    if (!confirmed) return;

    try {
      const response = await apiCall('delete', `api/po-rules/${id}`);
      if (!response.success) throw new Error(response.message || 'Failed to delete PO rule.');
      fetchPoRules();
    } catch (error) {
      console.error('Delete PO rule error:', error);
      alert(error.response?.data?.message || error.message || 'Failed to delete PO rule.');
    }
  };

  // ==========================================================
  // BULK ACTIONS
  // ==========================================================
  const runBulk = async (action) => {
    if (selectedIds.length === 0) return;
    const verb = { approve: 'approve', sync: 'sync', delete: 'delete' }[action];
    
    if (action === 'delete' && !window.confirm(`Delete ${selectedIds.length} orders?`)) return;

    if (action === 'sync') {
      const invalidOrders = uploads.filter((upload) => selectedIds.includes(upload.id || upload._id || upload.mongoId) && !hasShipToAddress(upload));
      if (invalidOrders.length > 0) {
        alert(`${invalidOrders.length} selected PO(s) are missing a mandatory Ship To address. These orders cannot be synced.`);
        return;
      }
    }

    try {
      await apiCall('post', `api/po/history/bulk/${verb}`, { ids: selectedIds });
      setSelectedIds([]);
      fetchHistory();
    } catch (e) {
      alert(e.response?.data?.message || `Bulk ${verb} failed.`);
    }
  };

  // ==========================================================
  // SINGLE ORDER ACTIONS
  // ==========================================================
  const handleOrderDetails = (upload) => {
    setSelectedOrder(upload);
    setIsOrderDetailsOpen(true);
  };

  const closeOrderDetails = () => {
    setSelectedOrder(null);
    setIsOrderDetailsOpen(false);
  };

  // ==========================================================
  // FILTER & PAGINATION
  // ==========================================================
  const filteredUploads = useMemo(() => {
    return uploads.filter((u) => {
      if (!search.trim() && statusFilter === 'all') return true;

      const searchLower = search.toLowerCase().trim();

      const poNum = String(u.structuredData?.purchaseOrder?.poNumber || u.poNumber || '');
      const soNum = String(u.erpagSO || '');
      const custName = String(u.structuredData?.customer?.name || u.structuredData?.customerName || u.customer || '');
      const billToName = String(u.structuredData?.billTo?.name || '');
      const shipToName = String(u.structuredData?.shipTo?.name || '');
      const shipToText = String(getShipToAddressText(u) || '');
      const fileName = String(u.originalFileName || '');
      const source = String(u.source || '');
      const status = String(u.status || '');

      const items = getItems(u);
      const itemText = items.map((item) =>
        [
          item.sku,
          item.vin,
          item.itemNumber,
          item.item_sku,
          item.vendor_item_number,
          item.buyer_item_number,
          item.description,
        ]
          .filter(Boolean)
          .join(' ')
      ).join(' ');

      const combinedText = [
        poNum,
        soNum,
        custName,
        billToName,
        shipToName,
        shipToText,
        fileName,
        source,
        status,
        itemText,
      ].join(' ').toLowerCase();

      const matchesSearch = !searchLower || combinedText.includes(searchLower);

      let matchesStatus = true;
      if (statusFilter !== 'all') {
        const s = (u.status || '').toUpperCase();
        if (statusFilter === 'Queued') {
          matchesStatus = ['NEW', 'PROCESSING', 'PENDING', 'APPROVED', 'QUEUED', 'TEXT_EXTRACTED'].includes(s);
        } else if (statusFilter === 'Successful') {
          matchesStatus = ['SYNCED', 'COMPLETED'].includes(s);
        } else if (statusFilter === 'Failed') {
          matchesStatus = ['FAILED'].includes(s);
        } else if (statusFilter === 'Error') {
          matchesStatus = ['ERROR'].includes(s);
        }
      }

      return matchesSearch && matchesStatus;
    });
  }, [uploads, search, statusFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, uploads.length, itemsPerPage]);

  const totalPages = Math.ceil(filteredUploads.length / itemsPerPage) || 1;
  const paginatedUploads = filteredUploads.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <div className="min-h-screen bg-gray-100 p-4 sm:p-6 font-sans">

      {/* UPLOAD MODAL */}
      {isUploadModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="relative w-full max-w-2xl max-h-[92vh] flex flex-col bg-white rounded-3xl overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200">
            <button onClick={() => setIsUploadModalOpen(false)} className="absolute top-4 right-4 z-50 p-2 bg-white rounded-full shadow-md text-gray-500 hover:text-black border border-gray-100">
              <X className="w-5 h-5" />
            </button>
            <div className="flex-1 overflow-y-auto">
              <UploadCard
                selectedFile={selectedFile}
                onFileSelect={handleFileSelect}
                onRemoveFile={handleRemoveFile}
                onUpload={handleUpload}
                uploadProgress={uploadProgress}
                uploadStatus={uploadStatus}
                errorMessage={errorMessage}
              />
            </div>
          </div>
        </div>
      )}

      {/* PO RULES MODAL */}
      {isRulesModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-6xl max-h-[92vh] flex flex-col rounded-3xl bg-white shadow-2xl overflow-hidden animate-in fade-in zoom-in-95">
            
            <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-6 py-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">Configuration</p>
                <h2 className="mt-1 text-2xl font-bold text-gray-900">PO Rules</h2>
                <p className="mt-1 text-sm text-gray-500">Configure customer/family category-level ordering exceptions.</p>
              </div>
              <button onClick={() => setIsRulesModalOpen(false)} className="rounded-full border border-gray-200 p-2 text-gray-500 hover:text-black">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {ruleError && (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{ruleError}</div>
              )}

              <div className="mb-5 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">Category Exceptions</h3>
                  <p className="text-sm text-gray-500">Rules are applied per ERPAG category.</p>
                </div>
                <button onClick={openAddRule} className="rounded-2xl bg-black px-4 py-3 text-sm font-semibold text-white hover:bg-gray-800">
                  + Add PO Rule
                </button>
              </div>

              {isRuleFormOpen && (
                <div className="mb-6 rounded-2xl border border-gray-200 bg-gray-50 p-5">
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">{editingRuleId ? 'Edit PO Rule' : 'Add PO Rule'}</h3>
                    <button onClick={closeRuleForm} className="text-gray-500 hover:text-black"><X className="h-5 w-5" /></button>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Customer Family</label>
                      <select
                        value={ruleForm.customerFamily}
                        onChange={(e) => handleRuleChange('customerFamily', e.target.value)}
                        disabled={familiesLoading}
                        className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none focus:border-black disabled:bg-gray-100"
                      >
                        <option value="">{familiesLoading ? 'Loading families...' : '— Select Customer Family —'}</option>
                        {sortedFamilies.map((family) => (
                          <option key={family._id || family.name} value={family.name}>{family.name}</option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-gray-500">Select a family to apply the rule at family level.</p>
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Individual Customer</label>
                      <select
                        value={ruleForm.customerName}
                        onChange={(e) => handleRuleChange('customerName', e.target.value)}
                        disabled={!ruleForm.customerFamily || familyCustomers.length === 0}
                        className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none focus:border-black disabled:bg-gray-100 disabled:text-gray-400"
                      >
                        <option value="">{!ruleForm.customerFamily ? '— Select a family first —' : familyCustomers.length === 0 ? '— No customers in family —' : '— All customers in family —'}</option>
                        {familyCustomers.map((customer) => (
                          <option key={customer} value={customer}>{customer}</option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-gray-500">Leave blank to apply the rule at family level.</p>
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">ERPAG Category</label>
                      <select
                        value={ruleForm.erpagCategory}
                        onChange={(e) => handleRuleChange('erpagCategory', e.target.value)}
                        disabled={categoriesLoading}
                        className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none focus:border-black disabled:bg-gray-100 disabled:text-gray-400"
                      >
                        <option value="">{categoriesLoading ? 'Loading ERPAG categories...' : '— Select ERPAG Category —'}</option>
                        {erpCategories.map((category) => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Unit Ordering</label>
                      <select
                        value={ruleForm.unitOrdering}
                        onChange={(e) => handleRuleChange('unitOrdering', e.target.value)}
                        className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none focus:border-black"
                      >
                        {UOM_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Multiple{selectedUomOption?.requiresMultiple && <span className="ml-1 text-red-500">*</span>}
                      </label>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={ruleForm.multiple}
                        disabled={!selectedUomOption?.requiresMultiple}
                        onChange={(e) => handleRuleChange('multiple', e.target.value)}
                        placeholder="e.g. 12"
                        className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none disabled:bg-gray-100 disabled:text-gray-400 focus:border-black"
                      />
                      {selectedUomOption?.requiresMultiple && (
                        <p className="mt-1 text-xs text-gray-500">Required for this UOM override.</p>
                      )}
                    </div>
                  </div>

                  {ruleForm.unitOrdering === 'UNIT_OVERRIDE' && (
                    <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
                      <strong>Unit override:</strong>
                      <div className="mt-1">PO Units ÷ Multiple = ERPAG Cases</div>
                      <div>Unit Cost × Multiple = Case Cost</div>
                    </div>
                  )}

                  {ruleForm.unitOrdering === 'CS_OVERRIDE' && (
                    <div className="mt-4 rounded-xl border border-purple-200 bg-purple-50 p-4 text-sm text-purple-800">
                      <strong>CS override:</strong>
                      <div className="mt-1">PO Cases × Multiple = ERPAG Units</div>
                      <div>Case Cost ÷ Multiple = Unit Cost</div>
                    </div>
                  )}

                  <div className="mt-5 flex justify-end gap-3">
                    <button onClick={closeRuleForm} disabled={ruleSaving} className="rounded-xl border border-gray-300 bg-white px-5 py-3 text-sm font-semibold text-gray-700">Cancel</button>
                    <button onClick={savePoRule} disabled={ruleSaving} className="rounded-xl bg-black px-5 py-3 text-sm font-semibold text-white disabled:opacity-50">
                      {ruleSaving ? 'Saving...' : editingRuleId ? 'Update Rule' : 'Save Rule'}
                    </button>
                  </div>
                </div>
              )}

              {ruleLoading ? (
                <div className="py-10 text-center text-sm text-gray-500">Loading PO rules...</div>
              ) : poRules.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-300 p-10 text-center">
                  <Settings className="mx-auto h-8 w-8 text-gray-400" />
                  <h3 className="mt-3 font-semibold text-gray-900">No PO rules configured</h3>
                  <p className="mt-1 text-sm text-gray-500">Add a category exception to configure customer ordering rules.</p>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-2xl border border-gray-200">
                  <table className="min-w-full text-left text-sm whitespace-nowrap">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 font-semibold text-gray-600">Customer Family</th>
                        <th className="px-4 py-3 font-semibold text-gray-600">Customer</th>
                        <th className="px-4 py-3 font-semibold text-gray-600">ERPAG Category</th>
                        <th className="px-4 py-3 font-semibold text-gray-600">Unit Ordering</th>
                        <th className="px-4 py-3 font-semibold text-gray-600">Multiple</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {poRules.map((rule) => (
                        <tr key={rule._id || rule.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{rule.customerFamily || '—'}</td>
                          <td className="px-4 py-3 text-gray-700">{rule.customerName || 'All customers in family'}</td>
                          <td className="px-4 py-3 text-gray-700">{rule.erpagCategory}</td>
                          <td className="px-4 py-3 text-gray-700">{getRuleLabel(rule)}</td>
                          <td className="px-4 py-3 font-semibold text-gray-900">{rule.multiple || '—'}</td>
                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-2">
                              <button onClick={() => openEditRule(rule)} className="rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold hover:border-black">Edit</button>
                              <button onClick={() => deletePoRule(rule)} className="rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50">Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>
        </div>
      )}

      {/* ORDER DETAILS MODAL */}
      {isOrderDetailsOpen && selectedOrder && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-5xl max-h-[92vh] flex flex-col rounded-3xl bg-white shadow-2xl overflow-hidden animate-in fade-in zoom-in-95">
            
            <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-6 py-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">Purchase Order</p>
                <h2 className="mt-1 text-2xl font-bold">{selectedOrder.structuredData?.purchaseOrder?.poNumber || 'PO Details'}</h2>
              </div>
              <button onClick={closeOrderDetails} className="rounded-full border border-gray-200 p-2">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              
              {!hasShipToAddress(selectedOrder) ? (
                <div className="mb-6 flex gap-3 rounded-2xl border border-red-200 bg-red-50 p-4">
                  <AlertTriangle className="h-6 w-6 shrink-0 text-red-600" />
                  <div>
                    <h3 className="font-semibold text-red-800">Ship To Address Missing</h3>
                    <p className="mt-1 text-sm text-red-700">This PO cannot be imported/synced because the Ship To address is mandatory. The billing address cannot be used as a replacement.</p>
                  </div>
                </div>
              ) : (
                <div className="mb-6 flex gap-3 rounded-2xl border border-green-200 bg-green-50 p-4">
                  <CheckCircle className="h-6 w-6 shrink-0 text-green-600" />
                  <div>
                    <h3 className="font-semibold text-green-800">Ship To Address Valid</h3>
                    <p className="mt-1 text-sm text-green-700">{getShipToAddressText(selectedOrder)}</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="rounded-2xl border border-gray-200 p-4">
                  <p className="text-xs uppercase tracking-wider text-gray-400">Customer / Ship To</p>
                  <p className="mt-2 font-semibold text-gray-900">{getCustomerName(selectedOrder) || 'Not available'}</p>
                </div>
                <div className="rounded-2xl border border-gray-200 p-4">
                  <p className="text-xs uppercase tracking-wider text-gray-400">Bill To</p>
                  <p className="mt-2 font-semibold text-gray-900">{getBillToName(selectedOrder) || 'Not available'}</p>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="rounded-2xl border border-gray-200 p-4">
                  <p className="text-xs uppercase tracking-wider text-gray-400">Req. Delivery Date</p>
                  <p className="mt-2 font-semibold text-gray-900">
                    {selectedOrder.structuredData?.purchaseOrder?.deliveryDate || selectedOrder.structuredData?.purchaseOrder?.requestedDeliveryDate || selectedOrder.order_header?.delivery_date || '—'}
                  </p>
                </div>
                <div className="rounded-2xl border border-gray-200 p-4">
                  <p className="text-xs uppercase tracking-wider text-gray-400">Cancel Date</p>
                  <p className="mt-2 font-semibold text-gray-900">
                    {selectedOrder.structuredData?.purchaseOrder?.cancelDate || selectedOrder.structuredData?.purchaseOrder?.cancelByDate || '—'}
                  </p>
                </div>
              </div>

              <div className="mt-4 rounded-2xl border border-gray-200 p-4">
                <p className="text-xs uppercase tracking-wider text-gray-400">Ship To Address</p>
                <p className="mt-2 text-sm text-gray-800">{getShipToAddressText(selectedOrder) || 'Missing'}</p>
              </div>

              <div className="mt-6">
                <h3 className="mb-3 text-lg font-bold">PO Items</h3>
                <div className="overflow-x-auto rounded-2xl border border-gray-200">
                  <table className="min-w-full text-sm whitespace-nowrap">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left">SKU</th>
                        <th className="px-4 py-3 text-left">Description</th>
                        <th className="px-4 py-3 text-right">Qty</th>
                        <th className="px-4 py-3 text-right">UOM</th>
                        <th className="px-4 py-3 text-right">Cost</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {getItems(selectedOrder).map((item, index) => (
                        <tr key={item.lineNumber || index}>
                          <td className="px-4 py-3">{item.sku || item.vin || item.itemNumber || '—'}</td>
                          <td className="px-4 py-3">{item.description || '—'}</td>
                          <td className="px-4 py-3 text-right">{item.quantity ?? item.qty ?? '—'}</td>
                          <td className="px-4 py-3 text-right">{item.unit || item.uom || '—'}</td>
                          <td className="px-4 py-3 text-right font-semibold">{item.unitCost ?? item.price ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {selectedOrder.structuredData?.items?.some((item) => item.uomRule || item.appliedUomRule || item.erpagQty || item.convertedQty) && (
                <div className="mt-6">
                  <h3 className="mb-3 text-lg font-bold">ERPAG UOM Conversion</h3>
                  <div className="overflow-x-auto rounded-2xl border border-gray-200">
                    <table className="min-w-full text-sm whitespace-nowrap">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left">Category</th>
                          <th className="px-4 py-3 text-left">Rule</th>
                          <th className="px-4 py-3 text-right">Original Qty</th>
                          <th className="px-4 py-3 text-right">ERPAG Qty</th>
                          <th className="px-4 py-3 text-right">Original Cost</th>
                          <th className="px-4 py-3 text-right">ERPAG Cost</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedOrder.structuredData.items.map((item, index) => (
                          <tr key={item.lineNumber || index}>
                            <td className="px-4 py-3">{item.erpAGCategory || item.erpagCategory || item.category || '—'}</td>
                            <td className="px-4 py-3">{item.appliedUomRule ? getRuleLabel(item.appliedUomRule) : item.uomRule ? getRuleLabel(item.uomRule) : 'Default'}</td>
                            <td className="px-4 py-3 text-right">{item.qty ?? item.quantity ?? '—'}</td>
                            <td className="px-4 py-3 text-right font-semibold">{item.erpAGQty ?? item.erpagQty ?? item.convertedQty ?? '—'}</td>
                            <td className="px-4 py-3 text-right">{item.unitCost ?? '—'}</td>
                            <td className="px-4 py-3 text-right font-semibold">{item.erpAGCost ?? item.erpagCost ?? item.convertedCost ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      )}

      {/* MAIN PAGE */}
      <div className="w-full max-w-[1920px] px-2 sm:px-4 lg:px-8 mx-auto space-y-6">

        {/* HEADER */}
        <section className="rounded-3xl border border-gray-200 bg-gray-50 p-6 text-black shadow-lg">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.3em] text-gray-400">Sales</p>
              <h1 className="mt-2 text-3xl font-bold">ERPAG Automated Orders</h1>
              <p className="mt-2 text-sm text-gray-500">Automated PO import, customer rules and ERPAG synchronization.</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button onClick={() => setIsUploadModalOpen(true)} className="inline-flex items-center justify-center gap-2 rounded-2xl border border-black px-4 py-3 text-sm font-semibold text-black hover:bg-gray-200 transition-colors">
                <UploadIcon className="w-4 h-4" />
                Upload PO Doc
              </button>
              <button
                onClick={() => {
                  setIsRulesModalOpen(true);
                  fetchPoRules();
                  fetchCustomerFamilies();
                  fetchCategories(); 
                }}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-black px-4 py-3 text-sm font-semibold text-black hover:bg-gray-200 transition-colors"
              >
                <Settings className="w-4 h-4" />
                PO Rules
              </button>
              <button onClick={() => runBulk('approve')} disabled={selectedIds.length === 0} className="rounded-2xl bg-black border border-black px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">
                Bulk Approve
              </button>
              <button onClick={() => runBulk('sync')} disabled={selectedIds.length === 0} className="rounded-2xl bg-black border border-black px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">
                Bulk Sync
              </button>
              <button onClick={() => runBulk('delete')} disabled={selectedIds.length === 0} className="rounded-2xl bg-black border border-black px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">
                Bulk Delete
              </button>
              {/*- Update SKU db --*/}
              <button
                onClick={handleRefreshSkus}
                disabled={isRefreshingSkus}
                className="inline-flex items-center justify-center gap-2 border border-indigo-300 rounded-2xl hover:bg-indigo-100 bg-indigo-50 text-indigo-700 px-4 py-3 text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshingSkus ? 'animate-spin' : ''}`} />
                {isRefreshingSkus ? 'Refreshing...' : 'Refresh SKU Database'}
              </button>
            </div>
          </div>
        </section>

        {/* SUCCESS */}
        {uploadStatus === 'success' && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl flex items-center justify-between">
            <span className="font-semibold text-sm">Document uploaded and processed successfully!</span>
            <button onClick={() => setUploadStatus('idle')} className="text-green-500 hover:text-green-700 font-bold">&times;</button>
          </div>
        )}

        {/* CONTENT */}
        <section className="rounded-3xl bg-white p-6 shadow-sm">
          
          <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="grid grid-cols-2 gap-3 lg:w-auto">
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs uppercase tracking-[0.2em] text-gray-500">Total Orders</p>
                <p className="mt-2 text-2xl font-bold text-black">{uploads.length}</p>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs uppercase tracking-[0.2em] text-gray-500">On Filter</p>
                <p className="mt-2 text-2xl font-bold text-black">{filteredUploads.length}</p>
              </div>
            </div>

            <div className="flex w-full flex-wrap gap-3 items-end lg:flex-1 lg:justify-end">
              <div className="flex flex-col min-w-[150px]">
                <label className="mb-1 text-sm font-medium text-gray-600">Status</label>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm text-black focus:border-black focus:outline-none">
                  <option value="all">All Statuses</option>
                  <option value="Queued">Queued</option>
                  <option value="Successful">Successful</option>
                  <option value="Failed">Failed</option>
                  <option value="Error">Error</option>
                </select>
              </div>

              <div className="flex flex-col min-w-[300px] flex-1 lg:max-w-xl">
                <label className="mb-1 text-sm font-medium text-gray-600">Search</label>
                <input type="text" placeholder="Search PO, Customer, Ship To, Status..." value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm text-black focus:border-black focus:outline-none" />
              </div>

              <button onClick={() => { setSearch(''); setStatusFilter('all'); }} className="rounded-2xl bg-black px-4 py-3 text-sm font-semibold text-white">
                Clear Filters
              </button>
            </div>
          </div>
 
          {filteredUploads.some((upload) => !hasShipToAddress(upload)) && (
            <div className="mb-5 flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3">
              <AlertTriangle className="h-5 w-5 shrink-0 text-red-600" />
              <div className="text-sm">
                <span className="font-semibold text-red-800">Ship To address required: </span>
                <span className="ml-1 text-red-700">One or more displayed POs are missing a valid Ship To address and cannot be synced.</span>
              </div>
            </div>
          )}

          <div className="border border-gray-200 rounded-xl overflow-hidden min-h-[300px] bg-white">
            {isInitialLoad ? (
              <div className="flex flex-col items-center justify-center h-[300px] text-gray-400">
                <Loader2 className="w-10 h-10 animate-spin mb-4 text-gray-300" />
                <p className="text-sm font-medium text-gray-500">Fetching orders...</p>
              </div>
            ) : (
              <UploadHistory
                uploads={paginatedUploads}
                onRefresh={fetchHistory}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                onOrderDetails={handleOrderDetails}
              />
            )}
          </div>

          {!isInitialLoad && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mt-6 px-2 gap-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <span className="text-sm text-gray-500 font-medium">
                  {selectedIds.length} selected | Showing {filteredUploads.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to {Math.min(currentPage * itemsPerPage, filteredUploads.length)} of {filteredUploads.length} entries
                </span>
                
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-500 font-medium">Rows per page:</label>
                  <select 
                    value={itemsPerPage} 
                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                    className="border border-gray-300 rounded-lg text-sm px-2 py-1 text-gray-700 bg-white outline-none focus:border-black cursor-pointer hover:bg-gray-50"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
              
              <div className="flex items-center gap-4 text-sm">
                <button
                  disabled={currentPage === 1 || filteredUploads.length === 0}
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  className="px-4 py-1.5 rounded-full border border-gray-300 text-gray-700 font-semibold bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                
                <span className="font-bold text-gray-900 bg-gray-100 px-3 py-1 rounded-lg">
                  Page {currentPage} of {totalPages}
                </span>
                
                <button
                  disabled={currentPage === totalPages || filteredUploads.length === 0}
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  className="px-4 py-1.5 rounded-full border border-gray-300 text-gray-700 font-semibold bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}

        </section>
      </div>
    </div>
  );
};

export default SalesERPag;